import Modal from "./DesignSystem/Modal/Modal";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { BOOKS_PLUS_PLAN } from "constants/subscriptionType";
import { useState } from "react";
import { Subscription } from "types/Models/subscription";
import Async from "./DesignSystem/AsyncComponents/Async";
import { CheckoutModal } from "./CheckoutModal/CheckoutModal";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { RocketPurpleAnimate } from "components/icons/RocketPurpleAnimate";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "./DesignSystem/ToggleGroup/ToggleGroup";
import { Tag } from "./DesignSystem/Tag/Tag";
import { Button } from "./DesignSystem/Button/Button";
import { Divider } from "./design/Divider";
import { SparkleGrey } from "./icons/SparkleGrey";
import { GreentickGreenCircle } from "./icons/GreentickGreenCircle";
import { TrialPeriodInfo } from "./BooksSoftwareSubscriptionsModal";

type BooksSubscriptionOnboardingModalProps = {
  open: boolean;
  onClose: () => void;
  entityId?: string;
  onSubscribed?: () => void;
};

const StandardPlanCard = ({
  selectedPlan,
  subscriptionPlans,
  onSelectPlan,
  onUpgrade,
  isLoading,
  trialEndDate,
}: {
  selectedPlan: Subscription;
  subscriptionPlans: Subscription[];
  onSelectPlan: (plan: Subscription) => void;
  onUpgrade: () => void;
  isLoading: boolean;
  trialEndDate?: string | null;
}) => {
  const monthlyOption = subscriptionPlans.find(
    (o) => o.billing_cycle === "MONTHLY"
  );
  const yearlyOption = subscriptionPlans.find(
    (o) => o.billing_cycle === "YEARLY"
  );
  const savingsPercent =
    monthlyOption && yearlyOption
      ? Math.round(
          ((monthlyOption.amount * 12 - yearlyOption.amount) /
            (monthlyOption.amount * 12)) *
            100
        )
      : null;

  const billingCycle =
    selectedPlan.billing_cycle === "MONTHLY" ? "month" : "year";
  const features = selectedPlan.metadata?.comparison?.filter((f) => f.active);

  return (
    <div className="t-border t-border-solid t-border-neutral-10 t-rounded-lg t-flex t-flex-col t-gap-4 t-px-5 t-py-6 t-relative t-overflow-hidden t-bg-surface">
      <div className="t-h-10">
        <RocketPurpleAnimate />
      </div>
      <div className="t-flex t-flex-col t-gap-1.5">
        <div className="t-text-subtitle-lg">
          {selectedPlan.subscription_name || "Standard"}
        </div>
        <div className="t-text-body-sm t-text-text-30">
          {selectedPlan.metadata?.service_description}
        </div>
      </div>
      <div className="t-flex t-items-center t-justify-between t-gap-4">
        <div className="t-text-subtext t-flex t-items-center t-text-text-60">
          <span>$</span>
          <span>{selectedPlan.amount}</span>
          <span className="t-text-subtext t-text-text-30">/{billingCycle}</span>
        </div>
        {subscriptionPlans.length > 1 && (
          <ToggleGroup
            defaultValue={selectedPlan.uuid}
            onValueChange={(value) => {
              const plan = subscriptionPlans.find((o) => o.uuid === value);
              if (plan) onSelectPlan(plan);
            }}
          >
            {subscriptionPlans.map((option) => (
              <ToggleGroupItem key={option.uuid} value={option.uuid}>
                {option.billing_cycle === "MONTHLY" ? (
                  "Monthly"
                ) : (
                  <span className="t-flex t-items-center t-gap-1.5">
                    Annually
                    {savingsPercent !== null && savingsPercent > 0 && (
                      <Tag tagType="purple" icon={false} size="small" rounded>
                        Save {savingsPercent}%
                      </Tag>
                    )}
                  </span>
                )}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}
      </div>
      {trialEndDate !== undefined && (
        <TrialPeriodInfo trialEndDate={trialEndDate} />
      )}
      <Button
        customType="primary_outlined"
        onClick={onUpgrade}
        isLoading={isLoading}
        block
      >
        Upgrade to Standard
      </Button>
      <Divider>
        <span className="t-flex t-gap-1 t-items-center">
          <SparkleGrey height={10} />
          <SparkleGrey />
          <SparkleGrey height={10} />
        </span>
      </Divider>
      {features && features.length > 0 && (
        <div className="t-flex t-flex-col t-gap-2">
          {features.map((feature) => (
            <div
              key={feature.description}
              className="t-flex t-gap-3 t-items-center t-text-text-30 t-text-body-sm"
            >
              <div className="t-w-4 t-h-4 t-flex t-items-center t-shrink-0">
                <GreentickGreenCircle className="t-text-green-70" />
              </div>
              <span>{feature.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const BooksSubscriptionOnboardingModal = ({
  open,
  onClose,
  entityId,
  onSubscribed,
}: BooksSubscriptionOnboardingModalProps) => {
  const {
    data: subscriptionPlans,
    isLoading,
    isSuccess,
  } = useGetSubscriptionsQuery({
    subscription_types: BOOKS_PLUS_PLAN,
  });
  const checkoutModal = useModal();
  const { isForeignCA } = useRoleBasedView();
  const [selectedPlan, setSelectedPlan] = useState<Subscription | undefined>();

  const standardPlans =
    subscriptionPlans?.filter(
      (item) => item.subscription_type === BOOKS_PLUS_PLAN
    ) || [];

  const displayPlan =
    selectedPlan ||
    standardPlans.find((p) => p.billing_cycle === "YEARLY") ||
    standardPlans[0];

  const handleSubscribed = () => {
    onSubscribed?.();
    checkoutModal.close();
    onClose();
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content size="regular">
        <Modal.Header>
          <Modal.Title>Upgrade Your Plan</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <Async.Root
            isEmpty={!subscriptionPlans || subscriptionPlans.length === 0}
            isLoading={isLoading}
            isSuccess={isSuccess}
          >
            <Async.Success>
              <div className="t-flex t-flex-col t-gap-4">
                <div className="t-flex t-flex-col t-gap-1">
                  <p className="t-m-0 t-text-body-sm t-text-text-30">
                    Upgrade to Standard Plan to connect unlimited banks and
                    access enhanced financial insights. You can adjust your plan
                    anytime.
                  </p>
                </div>
                {displayPlan && (
                  <StandardPlanCard
                    selectedPlan={displayPlan}
                    subscriptionPlans={standardPlans}
                    onSelectPlan={setSelectedPlan}
                    onUpgrade={checkoutModal.open}
                    isLoading={false}
                    trialEndDate={
                      displayPlan.is_trail_period_allowed
                        ? displayPlan.trail_period_end_date
                        : undefined
                    }
                  />
                )}
              </div>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
      {displayPlan && (
        <CheckoutModal
          entityId={entityId}
          type="subscription"
          onClose={checkoutModal.close}
          open={checkoutModal.isOpen}
          subscription={displayPlan}
          onSubscribed={handleSubscribed}
          payerUserType={isForeignCA && "FCA"}
        />
      )}
    </Modal.Root>
  );
};
